/**
 * View Utility Functions
 * Helper functions for viewing and displaying data in the Student Portfolio App
 */

/**
 * Color definitions for score ranges
 */
const SCORE_COLORS = {
  1: { light: '#6c573c', dark: '#574835' }, // Poor
  2: { light: '#9a7845', dark: '#826842' }, // Fair
  3: { light: '#cc9a3f', dark: '#b28945' }, // Good
  4: { light: '#FFBD39', dark: '#e7ab32' }, // Excellent
  5: { light: '#FFBD39', dark: '#e7ab32' }  // Excellent (5 is same as 4)
};

/**
 * Converts hex color to RGB object
 * @param {string} hex - Hex color string (e.g., '#FFBD39')
 * @returns {Object} RGB object with r, g, b properties
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 };
};

/**
 * Interpolates between two RGB colors
 * @param {Object} color1 - First RGB color
 * @param {Object} color2 - Second RGB color
 * @param {number} factor - Interpolation factor (0-1)
 * @returns {Object} Interpolated RGB color
 */
const interpolateRgb = (color1, color2, factor) => {
  return {
    r: Math.round(color1.r + (color2.r - color1.r) * factor),
    g: Math.round(color1.g + (color2.g - color1.g) * factor),
    b: Math.round(color1.b + (color2.b - color1.b) * factor)
  };
};

/**
 * Converts RGB object to CSS rgb string
 * @param {Object} rgb - RGB object with r, g, b properties
 * @returns {string} CSS rgb string
 */
const rgbToString = (rgb) => {
  return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
};

/**
 * Gets interpolated color based on exact score value
 * @param {number|null} score - The score value (1-5)
 * @param {string} shade - 'light' or 'dark' for gradient
 * @returns {string} RGB color string
 */
export const getInterpolatedScoreColor = (score, shade = 'light') => {
  if (typeof score !== 'number' || score < 1) {
    return shade === 'light' ? '#6c573c' : '#574835'; // Default to poor color
  }
  
  // Clamp score between 1 and 5
  const clampedScore = Math.max(1, Math.min(5, score));
  
  // Find the two color stops to interpolate between
  const lowerBound = Math.floor(clampedScore);
  const upperBound = Math.ceil(clampedScore);
  
  // If score is exactly on a stop, return that color
  if (lowerBound === upperBound) {
    return SCORE_COLORS[lowerBound][shade];
  }
  
  // Calculate interpolation factor
  const factor = clampedScore - lowerBound;
  
  // Get colors and interpolate
  const color1 = hexToRgb(SCORE_COLORS[lowerBound][shade]);
  const color2 = hexToRgb(SCORE_COLORS[upperBound][shade]);
  const interpolated = interpolateRgb(color1, color2, factor);
  
  return rgbToString(interpolated);
};

/**
 * Calculates brightness of an RGB color
 * @param {Object} rgb - RGB object with r, g, b properties
 * @returns {number} Brightness value (0-255)
 */



/**
 * Groups posts by grade level
 * @param {Array} posts - Array of post objects
 * @returns {Array} Array of objects with grade and posts properties
 */
export const groupPostsByGrade = (posts) => {
  const grouped = {};
  posts.forEach(post => {
    const grade = post.grade || 'Unknown';
    if (!grouped[grade]) {
      grouped[grade] = [];
    }
    grouped[grade].push(post);
  });
  
  // Sort grades in ascending order (8th grade first), then Portfolio Defense, Unknown last
  const sortedGrades = Object.keys(grouped).sort((a, b) => {
    // Handle 'Unknown' grade by putting it last
    if (a === 'Unknown') return 1;
    if (b === 'Unknown') return -1;
    // Portfolio Defense goes after all numeric grades
    if (a === 'Portfolio Defense') return 1;
    if (b === 'Portfolio Defense') return -1;
    // Numeric grades in ascending order (8th -> 12th)
    return parseInt(a) - parseInt(b);
  });
  
  return sortedGrades.map(grade => ({
    grade: grade,
    posts: grouped[grade].sort((a, b) => {
      // First sort by featured status (featured posts first)
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      // Then sort by timestamp (newest first)
      return new Date(b.timestamp) - new Date(a.timestamp);
    })
  }));
};

/**
 * Gets the title of a post by its ID
 * @param {string} postId - The post ID
 * @param {Array} posts - Array of post objects
 * @returns {string} The post title or 'Untitled Post'
 */
export const getPostTitle = (postId, posts) => {
  const post = posts.find(p => p.id === postId);
  return post ? post.title : 'Untitled Post';
};

/**
 * Gets all tag feedbacks for a specific post and tag
 * @param {string} postId - The post ID
 * @param {string} tag - The tag name
 * @param {Array} tagFeedbacks - Array of tag feedback objects
 * @returns {Array} Filtered array of tag feedbacks
 */
export const getPostTagFeedbacks = (postId, tag, tagFeedbacks) => {
  return (tagFeedbacks || []).filter(tf => tf.postId === postId && tf.tag === tag);
};

/**
 * Gets all feedbacks for a student, organized by tag
 * @param {string} studentId - The student ID
 * @param {Array} tagFeedbacks - Array of tag feedback objects
 * @returns {Object} Object with tags as keys and arrays of feedbacks as values
 */
export const getStudentFeedbackByTag = (studentId, tagFeedbacks) => {
  const byTag = {};
  (tagFeedbacks || [])
    .filter(tf => tf.studentId === studentId)
    .forEach(tf => {
      if (!byTag[tf.tag]) byTag[tf.tag] = [];
      byTag[tf.tag].push(tf);
    });
  return byTag;
};

/**
 * Computes the average score from an array of items with scores
 * @param {Array} items - Array of items with score property
 * @returns {number|null} Average score rounded to 1 decimal, or null if no scored items
 */
export const computeAverageScore = (items) => {
  if (!items || !Array.isArray(items)) return null;
  const scored = items.filter(i => typeof i.score === 'number');
  if (scored.length === 0) return null;
  const sum = scored.reduce((acc, i) => acc + i.score, 0);
  return Math.round((sum / scored.length) * 10) / 10;
};

/**
 * Gets the CSS class for a score value on transcripts
 * @param {number} score - The score value
 * @returns {string} CSS class name
 */
export const getScoreClass = (score) => {
  if (typeof score !== 'number') return 'transcript-score-none';
  if (score >= 4) return 'transcript-score-excellent';
  if (score >= 3) return 'transcript-score-good';
  if (score >= 2) return 'transcript-score-fair';
  return 'transcript-score-poor';
};

/**
 * Gets the CSS class for a skill average card
 * @param {number|null} average - The average score
 * @returns {string} CSS class name
 */
export const getSkillAverageCardClass = (average) => {
  if (average === null) return 'skill-average-card-none';
  if (average >= 4) return 'skill-average-card-excellent';
  if (average >= 3) return 'skill-average-card-good';
  if (average >= 2) return 'skill-average-card-fair';
  return 'skill-average-card-poor';
};

/**
 * Gets the CSS classes for skill average text elements
 * @param {number|null} average - The average score
 * @returns {string} Space-separated CSS class names
 */
export const getSkillAverageTextClass = (average) => {
  if (average === null) return 'skill-average-name-none skill-average-score-none skill-average-count-none';
  if (average >= 4) return 'skill-average-name-excellent skill-average-score-excellent skill-average-count-excellent';
  if (average >= 3) return 'skill-average-name-good skill-average-score-good skill-average-count-good';
  if (average >= 2) return 'skill-average-name-fair skill-average-score-fair skill-average-count-fair';
  return 'skill-average-name-poor skill-average-score-poor skill-average-count-poor';
};

/**
 * Checks if a post has been graded (has any feedback)
 * @param {string} postId - The post ID
 * @param {Array} tagFeedbacks - Array of tag feedback objects
 * @returns {boolean} True if post has feedback
 */
export const isPostGraded = (postId, tagFeedbacks) => {
  return tagFeedbacks.some(feedback => feedback.postId === postId);
};

/**
 * Gets all comments for a specific post
 * @param {string} postId - The post ID
 * @param {Array} comments - Array of comment objects
 * @returns {Array} Filtered array of comments
 */
export const getPostComments = (postId, comments) => {
  return (comments || []).filter(c => c.postId === postId);
};

/**
 * Filters posts based on user role and selected filters
 * @param {Object} params - Filter parameters
 * @param {Array} params.posts - Array of all posts
 * @param {Object} params.currentUser - Current user object
 * @param {Object} params.userProfile - User profile object
 * @param {string} params.selectedStudentUid - Selected student UID for filtering
 * @param {string} params.selectedTag - Selected tag for filtering
 * @returns {Array} Filtered array of posts
 */
export const getFilteredPosts = ({ posts, currentUser, userProfile, selectedStudentUid, selectedTag }) => {
  if (!currentUser || !userProfile) return [];

  const isTeacher = userProfile.role === 'teacher';

  let filtered = posts || []; // Start with all posts, default to empty array if undefined

  if (isTeacher) {
    // Teachers must select a specific student - if 'all' is selected, show no posts
    if (selectedStudentUid === 'all') {
      return []; // No posts shown until a specific student is selected
    }
    // Filter by the selected student's UID
    filtered = filtered.filter(p => p.userId === selectedStudentUid);
  } else { // If not a teacher, show only current user's posts
    filtered = filtered.filter(p => p.userId === currentUser.uid);
  }

  // Apply tag filter if active
  if (selectedTag !== 'all') {
    filtered = filtered.filter(p => 
      (p.tagJustifications || []).some(item => item.tag === selectedTag)
    );
  }
  return filtered;
};

